import { defaultThemePaletteOptions } from './palette'
import { defaultThemeSpacingOptions } from './defaultThemeSpacingOptions'
import { Components } from '@mui/material/styles/components'
import { alpha } from '@mui/material/styles'

const spacing = (level: number): number => defaultThemeSpacingOptions * level

export const defaultThemeComponentOptions: Components = {
  MuiCssBaseline: {
    styleOverrides: `
      .u-overflowOverlay {
        overflow-y: auto !important;
        overflow-y: overlay !important;
      }
      * {
        scrollbar-width: thin;
        scrollbar-color: ${defaultThemePaletteOptions.grey?.['250']} transparent;
        &:hover {
          ${defaultThemePaletteOptions.grey?.['400']};
        }
      }
      ::-webkit {
        &-scrollbar {
          width: ${spacing(1)}px;
          height: ${spacing(1)}px;
          background-color: transparent;
        }
        &-scrollbar-track {
          background: transparent;
          border: none;
          border-radius: 0;
        }
        &-scrollbar-thumb {
          background-color: ${defaultThemePaletteOptions.grey?.['250']};
          border: none;
          border-left: ${spacing(2)}px solid transparent;
          border-radius: 0;
          transition: .3s background-color;
          &:hover {
            background-color: ${defaultThemePaletteOptions.grey?.['400']};
          }
        }
      }
    `
  },
  MuiTab: {
    styleOverrides: {
      root: {
        padding: '0px',
        margin: '0px',
        minWidth: '0px',
        minHeight: '24px',
        fontWeight: '400',
        fontSize: '13px',
        lineHeight: '16px',
        letterSpacing: '0.01em',
        justifyContent: 'flex-start',
        '&:hover': { color: defaultThemePaletteOptions.text?.selected }
      }
    }
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: '24px'
      },
      flexContainer: {
        gap: spacing(3)
      }
    }
  },
  MuiButton: {
    styleOverrides: {
      root: {
        fontSize: 13,
        fontWeight: '400'
      },
      endIcon: {
        '&>*:nth-of-type(1)': {
          fontSize: 13
        }
      }
    }
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true
    }
  },
  MuiChip: {
    styleOverrides: {
      root: {
        '&:active': { boxShadow: 'none' }
      }
    }
  },
  MuiInput: {
    defaultProps: {
      disableUnderline: true
    },
    styleOverrides: {
      root: {
        borderTop: '1px solid transparent',
        borderBottom: `1px solid ${defaultThemePaletteOptions.grey?.['200']}`,
        height: spacing(8),
        transition: '.3s border, .3s color, .3s box-shadow',
        padding: '0',
        '&:placeholder': {
          color: defaultThemePaletteOptions.grey?.['600']
        },
        '&:hover': {
          boxShadow: '0 4px 4px -3px rgba(0,0,0,.07)'
        },
        '&.Mui-focused': {
          borderBottom: `1px solid ${defaultThemePaletteOptions.border?.primary}`,
          boxShadow: '0 4px 4px -3px rgba(0, 118, 168, 0.25)',
          background: 'transparent'
        },
        '& input': {
          padding: '0'
        },
        '& .MuiSelect-select:focus': {
          background: 'transparent'
        },
        '&.MuiInputBase-multiline': {
          height: 'auto'
        },
        '&.MuiAutocomplete-inputRoot': {
          minHeight: spacing(8),
          height: 'auto'
        }
      }
    }
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        minHeight: spacing(8),
        height: 'auto'
      }
    }
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        alignItems: 'center',
        padding: spacing(4),
        borderRadius: spacing(1),
        fontSize: '13px',
        color: defaultThemePaletteOptions.text?.primary,
        '&.MuiAlert-standardInfo': {
          border: `1px solid ${defaultThemePaletteOptions.grey?.['200']}`,
          borderLeft: `4px solid ${defaultThemePaletteOptions.grey?.['400']}`,
          background: defaultThemePaletteOptions.grey?.['100']
        },
        '&.MuiAlert-standardSuccess': {
          border: `1px solid ${defaultThemePaletteOptions.success?.['200']}`,
          borderLeft: `4px solid ${defaultThemePaletteOptions.success?.['main']}`,
          background: defaultThemePaletteOptions.success?.['100']
        },
        '&.MuiAlert-standardWarning': {
          border: `1px solid ${defaultThemePaletteOptions.warning?.['200']}`,
          borderLeft: `4px solid ${defaultThemePaletteOptions.warning?.['main']}`,
          background: defaultThemePaletteOptions.warning?.['100']
        },
        '&.MuiAlert-standardError': {
          border: `1px solid ${defaultThemePaletteOptions.error?.['200']}`,
          borderLeft: `4px solid ${defaultThemePaletteOptions.error?.['main']}`,
          background: defaultThemePaletteOptions.error?.['100']
        },
        '& .MuiAlert-message': {
          padding: 0
        },
        '& .MuiAlert-action': {
          flexShrink: 0,
          marginTop: '-8px',
          marginBottom: '-8px',
          paddingBlock: '0'
        }
      }
    }
  },
  MuiPopover: {
    defaultProps: {
      elevation: 0
    },
    styleOverrides: {
      root: {
        '& .MuiPaper-root': {
          boxShadow: '2px 2px 20px rgba(0, 0, 0, 0.05)',
          border: `1px solid ${defaultThemePaletteOptions.grey?.['200']}`
        }
      }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        height: spacing(7),
        padding: `0 ${spacing(3)}`,
        borderTop: '1px solid transparent',
        borderBottom: '1px solid transparent',
        transition: '.3s border, .3s background, .3s color, .3s box-shadow',
        '&:hover': {
          background: defaultThemePaletteOptions.action?.hover,
          borderTop: `1px solid ${defaultThemePaletteOptions.grey?.['200']}`,
          borderBottom: `1px solid ${defaultThemePaletteOptions.grey?.['200']}`
        },
        '&.Mui-selected': {
          background: defaultThemePaletteOptions.action?.selected,
          borderTop: `1px solid ${defaultThemePaletteOptions.border?.primary}`,
          borderBottom: `1px solid ${defaultThemePaletteOptions.border?.primary}`
        },
        '&:active': {
          boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1) inset'
        },
        '& .MuiListItemIcon-root': {
          marginRight: spacing(1),
          minWidth: spacing(5),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }
      }
    }
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        width: spacing(5),
        height: spacing(5),
        background: 'white',
        border: `1px solid ${defaultThemePaletteOptions.grey?.['200']}`,
        borderRadius: spacing(1),
        padding: 0,
        color: 'transparent',
        transition: '.3s color, .3s background',
        '&.Mui-checked': {
          color: defaultThemePaletteOptions.primary['main']
        },
        '& .MuiSvgIcon-root ': {
          width: '100%',
          height: '100%'
        }
      }
    }
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${defaultThemePaletteOptions.grey?.['200']}`,
        '&-head': {
          borderBottomWidth: '2px'
        },
        '&-hover:hover': {
          background: 'none'
        }
      }
    }
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontSize: '13px',
        padding: spacing(2),
        borderBottom: 'none',
        background: 'none',
        '&-head': {
          fontWeight: '700'
        }
      }
    }
  },
  MuiTablePagination: {
    styleOverrides: {
      selectLabel: {
        margin: '0px'
      },
      displayedRows: {
        margin: '0px'
      }
    }
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: spacing(1),
        border: `1px solid ${defaultThemePaletteOptions.grey?.['200']}`,
        boxShadow: 'none'
      }
    }
  },
  MuiCardHeader: {
    styleOverrides: {
      action: {
        alignSelf: 'initial',
        margin: 0
      }
    }
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '10px 13px',
        '&:last-child': {
          paddingBottom: '10px'
        }
      }
    }
  },
  MuiLink: {
    styleOverrides: {
      root: {
        '&:hover': {
          color: defaultThemePaletteOptions.hover?.primary
        }
      }
    }
  },
  MuiListItemAvatar: {
    styleOverrides: {
      root: {
        minWidth: 36,
        img: {
          display: 'block',
          borderRadius: '50%'
        }
      }
    }
  },
  MuiFab: {
    styleOverrides: {
      root: {
        boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
        border: `1px solid ${defaultThemePaletteOptions.grey?.['250']}`,
        backgroundColor: 'white',
        '&:hover': {
          boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
          backgroundColor: 'white'
        }
      },
      primary: {
        backgroundColor: defaultThemePaletteOptions.primary?.['main'],
        color: defaultThemePaletteOptions.primary?.['contrastText'],
        '&:hover': {
          backgroundColor: defaultThemePaletteOptions.primary?.['main']
        }
      },
      sizeSmall: {
        width: 28,
        height: 28,
        minHeight: 28
      }
    }
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      root: {
        marginTop: spacing(1),
        fontSize: '11px',
        lineHeight: '1em'
      },
      separator: {
        marginLeft: spacing(1.25),
        marginRight: spacing(1.25)
      }
    }
  },
  MuiButtonGroup: {
    styleOverrides: {
      grouped: {
        '&:not(:first-child)': {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0
        }
      },
      groupedContainedPrimary: {
        '&:not(:last-of-type)': {
          borderColor: alpha(defaultThemePaletteOptions.primary?.['dark'], 0.1)
        }
      },
      root: {
        boxShadow: `0 1px 2px rgba(0,0,0,.13)`,
        '& .MuiInputBase-root': {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0
        }
      }
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        borderColor: defaultThemePaletteOptions.grey?.['250']
      }
    }
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        '&::before': {
          display: 'none'
        },
        boxShadow: 'none',
        background: 'none'
      }
    }
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        padding: 0,
        paddingBottom: spacing(2),
        minHeight: 'auto',
        borderBottom: `1px solid ${defaultThemePaletteOptions.grey?.['200']}`,
        '&.Mui-expanded': {
          minHeight: 'auto'
        }
      },
      content: {
        margin: 0
      }
    }
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        padding: 0,
        paddingTop: spacing(4),
        paddingBottom: spacing(2)
      }
    }
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontSize: '11px'
      }
    }
  }
}
